import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {BehaviorSubject, map, Observable, tap} from "rxjs";
import {ActionResult, ActionResultList, RequestFilter, UserDetail, Request, PagedFilter} from "@model";
import {LoaderService} from "./loader.service";
import {ValidatorResult} from "../model/validation/ValidationError";
import {EnvironmentService} from "./environment.service";

@Injectable({ providedIn:'root' })
export class RequestService {

  private static _fetchNewRequests: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private static _currentRequest: BehaviorSubject<Request | undefined> = new BehaviorSubject<Request | undefined>(undefined);

  get fetchNewRequests(): BehaviorSubject<boolean | null> {
    return RequestService._fetchNewRequests;
  }

  get currentRequest(): BehaviorSubject<Request | undefined> {
    return RequestService._currentRequest;
  }

  constructor(private http: HttpClient, private loaderService: LoaderService, private environmentService:EnvironmentService) {
  }

  sendPublishEventRequest(request: Request, onSuccess: () => void, onError: () => void): Observable<ValidatorResult | null> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<ValidatorResult>>(this.environmentService.getApiUrl() + "/api/requests/event/new", request).pipe(
      tap(() => {
        this.loaderService.dismissLoading();
      }),
      map((value: ActionResult<ValidatorResult>) => {
        if (value && value.result) {
          const result: ValidatorResult = ValidatorResult.deserialize(value.result)
          if (result.hasErrors()) {
            return result;
          } else {
            onSuccess();
            return null;
          }
        } else {
          onError()
          return null;
        }
      }));
  }

  manageEventRequest(request: Request): Observable<ValidatorResult | undefined> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<ValidatorResult>>(this.environmentService.getApiUrl() + "/api/requests/manage/event", request).pipe(tap(() => {
        this.loaderService.dismissLoading();
      }),
      map((value: ActionResult<ValidatorResult>) => {
        if (value && value.result) {
          return ValidatorResult.deserialize(value.result);
        } else {
          return undefined;
        }
      }));
  }

  sendOrganiserRequest(request: Request, onSuccess: () => void, onError: () => void) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/requests/organiser/new", request)
      .pipe(tap(() => this.loaderService.dismissLoading())).subscribe((actionResult: ActionResult<boolean>) => {
      if (actionResult.resultCode !== -1) {
        onSuccess();
      } else {
        onError();
      }
    });
  }

  manageOrganiserRequest(request: Request): Observable<boolean> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/requests/manage/organiser", request).pipe(tap(() => {
        this.loaderService.dismissLoading()
      }),
      map((value: ActionResult<boolean>) => {
        return value.result ?? false;
      }));
  }

  manageAddEventToLeagueRequest(request: Request): Observable<boolean> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/requests/manage/league", request).pipe(tap(() => {
        this.loaderService.dismissLoading()
      }),
      map((value: ActionResult<boolean>) => {
        return value.result ?? false;
      }));
  }

  filterRequests(requestFilter: PagedFilter<RequestFilter>): Observable<Request[]> {
    return this.http.post<ActionResultList<Request[], number>>(this.environmentService.getApiUrl() + "/api/requests/filter", requestFilter).pipe(map((value: ActionResultList<Request[], number>) => {
      if (value && value.result && value.result.length != 0) {
        return value.result;
      } else {
        return [];
      }
    }));
  }

  fetchRequestById(requestId: number | undefined, hideLoading?: boolean): Observable<Request | null> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResult<Request>>(this.environmentService.getApiUrl() + "/api/requests/fetchbyid", requestId).pipe(tap(() => {
      this.loaderService.dismissLoading();
    }), map((value: ActionResult<Request>) => {
      if (value && value.result) {
        this.currentRequest.next(value.result);
        return value.result;
      } else {
        this.currentRequest.next(undefined);
        return null;
      }
    }));
  }

  fetchRequestUser(userId?: number | undefined): Observable<UserDetail | undefined> {
    return this.http.post<ActionResult<UserDetail>>(this.environmentService.getApiUrl() + "/api/requests/userdetails", userId ?? null).pipe(
      map((value: ActionResult<UserDetail>) => {
        if (value) {
          return value.result;
        } else {
          return undefined;
        }
      })
    );
  }
}
